import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  ArrowLeft,
  Loader2,
  Clock,
  Info,
  PiggyBank,
  Receipt,
  Upload,
  File,
  Trash2,
  AlertTriangle,
  Send,
  Wallet,
  Smartphone,
  Landmark,
  Banknote,
  Search,
  Eye,
  ChevronRight,
  Pencil,
  X,
} from "lucide-react";

import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { getDefaultAvatar } from "@/utils/getDefaultAvatar";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const REQUIRED_MONTHLY = 10000;

const safeNumber = (value: any): number => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const isApprovedStatus = (status: any) => {
  const normalized = String(status || "").toLowerCase().replace(/\s+/g, "_");
  return ["verified", "completed", "approved", "deducted_from_salary", "submitted_to_hr", "confirmed_by_accountant", "approved_by_president", "verified_by_secretary"].includes(normalized);
};



export default function RecordSaving() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formType, setFormType] = useState("Savings");
  const [formAmount, setFormAmount] = useState("");
  const [formMethod, setFormMethod] = useState("");
  const [formDate, setFormDate] = useState<Date>(new Date());
  const [isEditing, setIsEditing] = useState(false);
  const [formNotes, setFormNotes] = useState("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const today = new Date();
  const [paidMonth, setPaidMonth] = useState<string>(String(today.getMonth()));
  const [paidYear, setPaidYear] = useState<string>(String(today.getFullYear()));
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedLoanId, setSelectedLoanId] = useState<number | null>(null);
  const [selectedPenaltyId, setSelectedPenaltyId] = useState<number | null>(null);
  const [historySearch, setHistorySearch] = useState("");
  const [historyStatusFilter, setHistoryStatusFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const normalizeStatus = (raw: any) => {
    if (!raw) return raw;
    const d = new Date();
    const month = raw.month ?? d.getMonth();
    const year = raw.year ?? d.getFullYear();
    const minAmount = raw.minimumMonthlyContribution ?? raw.minimumAmount ?? 10000;
    const cutoffDay = raw.payrollCutoffDay ?? raw.cutoffDay ?? 25;
    const cutoffDate = raw.cutoffDate ? new Date(raw.cutoffDate) : new Date(year, month, cutoffDay);
    const isPastCutoff = raw.isPastCutoff ?? (month === d.getMonth() && year === d.getFullYear() && d.getDate() > cutoffDay);
    return {
      month,
      year,
      minimumAmount: minAmount,
      cutoffDay,
      cutoffDate: cutoffDate.toISOString?.() ?? cutoffDate,
      isPastCutoff,
      canDeclare: raw.canDeclare ?? !isPastCutoff,
      isLateDeclaration: raw.isLateDeclaration ?? false,
      cutoffPassed: raw.cutoffPassed ?? isPastCutoff,
      payrollBatchStatus: raw.payrollBatchStatus ?? "open",
      message: raw.message ?? null,
      declaration: raw.existingDeclaration ?? raw.declaration ?? null,
      approvalCount: raw.approvalCount ?? 0,
      requiredApprovalCount: raw.requiredApprovalCount ?? 3,
      payrollStatus: raw.payrollStatus === "not_started" ? "none" : (raw.payrollStatus ?? "none"),
      status: raw.status ?? "not_declared",
      statusLabel: raw.statusLabel ?? (raw.status === "not_declared" ? "Not Declared" : raw.status ?? "Not Declared"),
      finalSavedAmount: raw.finalSavedAmount ?? 0,
      verifiedContribution: raw.verifiedContribution ?? null,
      payrollDeductionEnabled: raw.payrollDeductionEnabled ?? true,
    };
  };

  const {
    data: monthlyStatus,
    isLoading: loadingStatus,
  } = useQuery(
    ["record-saving-status", user?.id],
    async () => {
      const d = new Date();
      const { data } = await api.get(`/member/monthly-saving-status?month=${d.getMonth()}&year=${d.getFullYear()}`);
      return normalizeStatus(data.data || data);
    },
    { enabled: !!user?.id, retry: 2, staleTime: 30000 }
  );

  const { data: settingsData } = useQuery(
    ["record-saving-settings"],
    async () => {
      const { data } = await api.get("/settings");
      return data.data || data;
    },
    { staleTime: 60000, retry: 1 }
  );

  const payrollEnabled = monthlyStatus?.payrollDeductionEnabled ?? settingsData?.payroll?.payrollDeductionEnabled !== false;

  const { data: activeLoansData, isLoading: loadingLoans } = useQuery(
    ["record-loans", user?.id],
    async () => {
      const { data } = await api.get("/member/me/active-loans");
      return data.data;
    },
    { enabled: formType === "Loan Repayment" && !!user?.id, retry: 1 }
  );

  const { data: unpaidPenaltiesData, isLoading: loadingPenalties } = useQuery(
    ["record-penalties", user?.id],
    async () => {
      const { data } = await api.get("/member/me/unpaid-penalties");
      return data.data;
    },
    { enabled: formType === "Fine" && !!user?.id, retry: 1 }
  );

  const { data: memberPayments = [], isLoading: loadingMemberPayments } = useQuery(
    ["record-member-payments", user?.id],
    async () => {
      const { data } = await api.get("/member-payments/my");
      return data.data || [];
    },
    { enabled: !!user?.id, retry: 1, staleTime: 30000 }
  );

  const { data: contributionsData = [], isLoading: loadingContributions } = useQuery(
    ["record-member-contributions", user?.id],
    async () => {
      const { data } = await api.get("/contributions/my?limit=500");
      return (data.data || []) as any[];
    },
    { enabled: !!user?.id, retry: 1, staleTime: 30000 }
  );

  // Form starts empty - member must explicitly enter an amount to prevent accidental submissions

  const resetForm = () => {
    setFormType("Savings");
    setFormAmount("");
    setFormMethod("");
    setFormDate(new Date());
    setFormNotes("");
    setFormErrors({});
    setAttachmentFile(null);
    setSelectedLoanId(null);
    setSelectedPenaltyId(null);
    const t = new Date();
    setPaidMonth(String(t.getMonth()));
    setPaidYear(String(t.getFullYear()));
  };

  const computedAmount = useMemo(() => {
    if (formType === "Monthly Share") {
      const amt = parseFloat(formAmount);
      if (amt > 0) return amt;
      if (monthlyStatus?.minimumAmount) return monthlyStatus.minimumAmount;
      return REQUIRED_MONTHLY;
    }
    if (formType === "Loan Repayment" && selectedLoanId && activeLoansData?.length) {
      const loan = Array.isArray(activeLoansData) ? activeLoansData.find((l: any) => l.id === selectedLoanId) : null;
      return loan?.monthlyPayment || 0;
    }
    if (formType === "Fine" && selectedPenaltyId && unpaidPenaltiesData?.length) {
      const penalty = Array.isArray(unpaidPenaltiesData) ? unpaidPenaltiesData.find((p: any) => p.id === selectedPenaltyId) : null;
      return penalty?.amount || 0;
    }
    return parseFloat(formAmount) || 0;
  }, [formType, selectedLoanId, selectedPenaltyId, formAmount, monthlyStatus, activeLoansData, unpaidPenaltiesData]);

  const isFormValid = useMemo(() => {
    if (!formType) return false;
    if (formType === "Monthly Share") {
      if (loadingStatus) return false;
      const min = monthlyStatus?.minimumAmount || REQUIRED_MONTHLY;
      const amt = parseFloat(formAmount) || min;
      if (amt < min) return false;
      if (monthlyStatus?.canDeclare === false) return false;
      return true;
    }
    if (formType === "Loan Repayment" && !selectedLoanId) return false;
    if (formType === "Fine" && !selectedPenaltyId) return false;
    if (formType === "Savings" && (!formAmount || parseFloat(formAmount) <= 0)) return false;
    if (!formMethod) return false;
    if (!formDate) return false;
    if (paidMonth === "" || paidYear === "") return false;
    if (computedAmount <= 0) return false;
    return true;
  }, [formType, monthlyStatus, formAmount, selectedLoanId, selectedPenaltyId, formMethod, formDate, paidMonth, paidYear, computedAmount, loadingStatus]);

  const getDisabledReason = () => {
    if (!formType) return "Select a saving type";
    if (formType === "Monthly Share") {
      if (loadingStatus) return "Loading declaration settings...";
      if (!monthlyStatus) return "Loading declaration settings...";
      if (monthlyStatus?.canDeclare === false) return monthlyStatus?.message || "Payroll list has been sent to HR.";
      const min = monthlyStatus?.minimumAmount || REQUIRED_MONTHLY;
      const amt = parseFloat(formAmount) || min;
      if (formAmount && amt < min) return `Minimum is ${min.toLocaleString()} FRW`;
      return undefined;
    }
    if (formType === "Loan Repayment" && !selectedLoanId) {
      const hasLoans = Array.isArray(activeLoansData) && activeLoansData.length > 0;
      return hasLoans ? "Select a loan to repay" : "No active loan available";
    }
    if (formType === "Fine" && !selectedPenaltyId) {
      const hasPenalties = Array.isArray(unpaidPenaltiesData) && unpaidPenaltiesData.length > 0;
      return hasPenalties ? "Select a fine to pay" : "No unpaid fines";
    }
    if (computedAmount <= 0) return "Amount must be greater than 0";
    if (formType !== "Monthly Share" && !formMethod) return "Select a payment method";
    if (formType !== "Monthly Share" && !formDate) return "Select a payment date";
    return "";
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formType) errors.type = "Saving type is required";
    if (formType === "Monthly Share") {
      const min = monthlyStatus?.minimumAmount || REQUIRED_MONTHLY;
      const amt = parseFloat(formAmount) || min;
      if (amt < min) errors.amount = `Minimum amount is ${min.toLocaleString()} FRW`;
      if (monthlyStatus?.canDeclare === false) errors.type = monthlyStatus?.message || "Cannot declare now.";
    }
    if (formType === "Loan Repayment" && !selectedLoanId) errors.type = "Select a loan to repay";
    if (formType === "Fine" && !selectedPenaltyId) errors.type = "Select a fine to pay";
    if (formType === "Savings" && (!formAmount || parseFloat(formAmount) <= 0)) errors.amount = "Amount must be greater than 0";
    if (computedAmount <= 0) errors.amount = "Amount must be greater than 0";
    if (formType !== "Monthly Share") {
      if (!formMethod) errors.method = "Payment method is required";
      if (!formDate) errors.date = "Saving date is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const recordMutation = useMutation(
    async () => {
      let attachmentUrl = "";
      if (attachmentFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append("files", attachmentFile);
        const uploadRes = await api.post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        attachmentUrl = uploadRes.data?.fileUrl || "";
        setUploading(false);
      }

      const paymentType = formType === "Monthly Share" || formType === "Savings" ? "saving"
        : formType === "Loan Repayment" ? "loan_payment"
          : formType === "Fine" ? "penalty_payment"
            : "saving";

      if (formType === "Monthly Share") {
        const declaredAmount = parseFloat(formAmount) || monthlyStatus?.minimumAmount || REQUIRED_MONTHLY;
        const targetMonth = monthlyStatus?.month ?? new Date().getMonth();
        const targetYear = monthlyStatus?.year ?? new Date().getFullYear();
        const { data } = await api.post("/member/monthly-saving-declarations", {
          declaredAmount,
          month: targetMonth,
          year: targetYear,
          isLateDeclaration: monthlyStatus?.isLateDeclaration ?? false,
          cutoffPassed: monthlyStatus?.cutoffPassed ?? false,
          payrollBatchStatusAtSubmission: monthlyStatus?.payrollBatchStatus ?? "open",
        });
        return data;
      }

      const payload: Record<string, any> = {
        type: paymentType,
        amount: computedAmount,
        paidMonth: parseInt(paidMonth),
        paidYear: parseInt(paidYear),
        paymentDate: format(formDate, "yyyy-MM-dd"),
        paymentMethod: formMethod,
      };
      if (formNotes) payload.notes = formNotes;
      if (attachmentUrl) payload.attachmentUrl = attachmentUrl;

      const { data } = await api.post("/member-payments", payload);
      return data;
    },
    {
      onSuccess: (data) => {
        toast.success(data?.message || "Submitted successfully and is awaiting approval from cooperative leaders.");
        resetForm();
        queryClient.invalidateQueries(["record-member-payments"]);
        queryClient.invalidateQueries(["record-saving-status"]);
      },
      onError: (err: { response?: { status?: number; data?: { message?: string } } }) => {
        const status = err?.response?.status;
        const msg = err?.response?.data?.message;
        if (status === 401) toast.error("Session expired. Please login again.");
        else if (status === 403) toast.error(msg || "Your account is not linked to a member profile.");
        else if (msg) toast.error(msg);
        else toast.error("Unable to submit. Please try again.");
      },
    }
  );

  const handleSubmit = () => {
    if (validateForm()) {
      recordMutation.mutate();
    }
  };

  const { data: memberProfile } = useQuery(
    ["record-saving-profile", user?.id],
    async () => {
      const { data } = await api.get("/members/me/profile");
      return data.data || data;
    },
    { enabled: !!user?.id, staleTime: 60000 }
  );

  const shareValue = safeNumber(settingsData?.savings?.shareValue) || safeNumber((user as any)?.group?.pricePerShare) || REQUIRED_MONTHLY;

  const approvedSavings = useMemo(() => {
    // ALL contributions (no status filter)
    const fromContributions = (contributionsData as any[])
      .reduce((sum: number, c: any) => sum + safeNumber(c.amount || c.depositAmount), 0);
    // Approved savings from member-submitted payments
    const fromPayments = (memberPayments as any[])
      .filter((payment: any) => payment.type === "saving" && String(payment.status || "").toLowerCase() === "approved")
      .reduce((sum: number, payment: any) => sum + safeNumber(payment.amount), 0);
    // Admin-set savingAmount from profile
    const memberSavingAmount = safeNumber(memberProfile?.savingAmount);
    return fromContributions + fromPayments + memberSavingAmount;
  }, [memberPayments, contributionsData, memberProfile]);

  const totalAllSavings = approvedSavings;

  const voluntarySavings = useMemo(() => {
    const fromPayments = (memberPayments as any[])
      .filter((payment: any) => payment.type === "saving" && String(payment.status || "").toLowerCase() === "approved")
      .reduce((sum: number, payment: any) => sum + safeNumber(payment.amount), 0);
    const memberSavingAmount = safeNumber(memberProfile?.savingAmount);
    return fromPayments + memberSavingAmount;
  }, [memberPayments, memberProfile]);

  const voluntarySavingsShares = shareValue > 0 ? Math.floor(voluntarySavings / shareValue) : 0;

  const pendingSavingsPayments = useMemo(() => {
    return (memberPayments as any[]).filter((payment: any) => payment.type === "saving" && payment.status === "pending_approval");
  }, [memberPayments]);

  const pendingSavings = useMemo(() => {
    return pendingSavingsPayments.reduce((sum: number, payment: any) => sum + safeNumber(payment.amount), 0);
  }, [pendingSavingsPayments]);

  const savingsShares = shareValue > 0 ? Math.floor(approvedSavings / shareValue) : 0;
  const pendingSavingsShares = shareValue > 0 ? Math.floor(pendingSavings / shareValue) : 0;

  const savingsHistory = useMemo(() => {
    // Member-submitted savings (via member-payments flow)
    const paymentRows = (memberPayments as any[])
      .filter((payment: any) => payment.type === "saving" && payment.amount > 0)
      .map((payment: any) => {
        const approvals = payment.approvals || [];
        const approvedApprovals = approvals.filter((a: any) => a.status === "approved");
        const lastApproval = approvedApprovals[approvedApprovals.length - 1];
        return {
          id: `payment-${payment.id}`,
          date: payment.paymentDate || payment.submittedAt || payment.createdAt,
          amount: safeNumber(payment.amount),
          shares: shareValue > 0 ? Math.floor(safeNumber(payment.amount) / shareValue) : 0,
          method: payment.paymentMethod || "Manual",
          proof: payment.attachmentUrl || "",
          note: payment.notes || "Saving request",
          status: payment.status,
          approvedBy: payment.status === "approved" && lastApproval
            ? lastApproval.leaderName || "System"
            : payment.status === "rejected"
              ? "System"
              : `${approvedApprovals.length}/${payment.requiredApprovals || 3} approvals`,
          approvalDate: payment.status === "approved" && lastApproval
            ? lastApproval.createdAt
            : payment.status === "rejected"
              ? payment.updatedAt
              : "",
        };
      });

    // Admin-recorded savings (all contributions)
    const adminSavingRows = (contributionsData as any[])
      .filter((c: any) => safeNumber(c.amount) > 0)
      .map((c: any) => ({
        id: `contribution-${c.id}`,
        date: c.contributionDate || c.createdAt,
        amount: safeNumber(c.amount),
        shares: shareValue > 0 ? Math.floor(safeNumber(c.amount) / shareValue) : 0,
        method: c.paymentMethod || "Cash",
        proof: c.bankReceipt || "",
        note: c.notes || "Admin saving record",
        status: isApprovedStatus(c.status) ? "approved" : (c.status || "pending").toLowerCase(),
        approvedBy: c.createdBy || "Admin",
        approvalDate: c.updatedAt || c.createdAt || "",
      }));

    return [...paymentRows, ...adminSavingRows]
      .sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
  }, [memberPayments, contributionsData, shareValue]);

  const filteredHistory = useMemo(() => {
    const q = historySearch.trim().toLowerCase();
    return savingsHistory.filter((row) => {
      const normalizedStatus = String(row.status || "").toLowerCase();
      const matchesSearch = !q || [
        row.method,
        row.note,
        row.approvedBy,
        row.amount?.toString(),
      ].join(" ").toLowerCase().includes(q);
      const matchesStatus =
        historyStatusFilter === "all" ||
        (historyStatusFilter === "pending" && (normalizedStatus.includes("pending") || normalizedStatus === "pending_approval")) ||
        (historyStatusFilter === "approved" && isApprovedStatus(row.status)) ||
        (historyStatusFilter === "rejected" && (normalizedStatus.includes("rejected") || normalizedStatus.includes("cancelled")));
      return matchesSearch && matchesStatus;
    });
  }, [savingsHistory, historySearch, historyStatusFilter]);

  const loadingSavingsData = loadingMemberPayments || loadingContributions;

  const getDisplayName = () => {
    const rawName = (user?.fullName || user?.name || "").trim();
    const fallback = user?.firstName || rawName || "Member";
    const knownRoleSuffixes = ["Technical Support", "President", "Secretary", "Accountant", "Admin", "Member"];
    for (const suffix of knownRoleSuffixes) {
      const regex = new RegExp(`\\s+${suffix}$`, "i");
      if (regex.test(rawName)) {
        const cleaned = rawName.replace(regex, "").trim();
        return cleaned || fallback;
      }
    }
    return fallback;
  };

  const getRoleDisplayName = () => {
    const leadershipRole = user?.leadershipRole || user?.role?.name || (typeof user?.role === "string" ? user.role : null);
    if (leadershipRole && ["Technical Support", "President", "Secretary", "Accountant"].includes(leadershipRole)) return leadershipRole;
    if (user?.isAdmin) return "Admin";
    return leadershipRole || "Member";
  };

  const get2LetterInitials = (name: string) => {
    const [firstName, lastName] = name.split(" ");
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`;
  };

  const currentYear = new Date().getFullYear();
  const thisYearTotalSavings = useMemo(() => {
    const savingsYear = (memberPayments as any[])
      .filter((payment: any) => {
        if (payment.type !== "saving" || payment.status !== "approved") return false;
        const d = new Date(payment.paymentDate || payment.submittedAt || payment.createdAt);
        return d.getFullYear() === currentYear;
      })
      .reduce((sum: number, payment: any) => sum + safeNumber(payment.amount), 0);
    const contribYear = (contributionsData as any[])
      .filter((c: any) => {
        const d = new Date(c.contributionDate || c.createdAt);
        return d.getFullYear() === currentYear && (c.isVerified === true || c.status === "Verified" || c.status === "Completed");
      })
      .reduce((sum: number, c: any) => sum + (Number(c.amount) || 0), 0);
    return savingsYear + contribYear;
  }, [memberPayments, contributionsData, currentYear]);



  return (
    <div className="min-h-screen bg-slate-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-4 sm:space-y-5">

        {/* ── PAGE HEADER ── */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate("/member/dashboard")}
              className="flex items-center justify-center h-9 w-9 shrink-0 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-700 transition-colors"
              title="Back to dashboard"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight truncate">My Savings</h1>
              <p className="text-xs sm:text-sm text-slate-500">Manage your voluntary savings</p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="hidden sm:flex items-center gap-2.5 text-right">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate max-w-[180px]">{getDisplayName()}</p>
                <p className="text-xs text-slate-500">{getRoleDisplayName()}</p>
              </div>
              <Avatar className="h-9 w-9 ring-2 ring-white shadow-sm shrink-0">
                <AvatarImage src={user?.profileUrl || getDefaultAvatar(user?.email || user?.name)} alt={getDisplayName()} />
                <AvatarFallback className="bg-[#009A44] text-white text-[11px] font-bold">
                  {get2LetterInitials(getDisplayName())}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>
        </div>

        {/* ── SUMMARY CARDS ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[
            {
              label: "Total in Account (All Savings)",
              value: `${totalAllSavings.toLocaleString()} FRW`,
              sub: `From contributions & voluntary savings`,
              icon: Wallet,
              bg: "bg-emerald-50",
              iconBg: "bg-emerald-100",
              iconColor: "text-[#059669]",
              border: "border-emerald-200/60",
            },
            {
              label: "Voluntary Savings (Approved)",
              value: `${voluntarySavings.toLocaleString()} FRW`,
              sub: `${voluntarySavingsShares} Share${voluntarySavingsShares !== 1 ? "s" : ""}`,
              icon: PiggyBank,
              bg: "bg-green-50",
              iconBg: "bg-green-100",
              iconColor: "text-[#16A34A]",
              border: "border-green-200/60",
            },
            {
              label: "Pending Savings",
              value: `${pendingSavings.toLocaleString()} FRW`,
              sub: `${pendingSavingsShares} Share${pendingSavingsShares !== 1 ? "s" : ""} awaiting approval`,
              icon: Clock,
              bg: "bg-blue-50",
              iconBg: "bg-blue-100",
              iconColor: "text-[#2563EB]",
              border: "border-blue-200/60",
            },
            {
              label: "Total Savings Shares",
              value: `${savingsShares} Shares`,
              sub: "From voluntary savings",
              icon: Banknote,
              bg: "bg-purple-50",
              iconBg: "bg-purple-100",
              iconColor: "text-[#7C3AED]",
              border: "border-purple-200/60",
            },
          ].map((card) => (
            <Card key={card.label} className={cn("group border bg-white rounded-xl shadow-sm hover:shadow transition-shadow", card.border)}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium text-slate-500 mb-0.5 truncate">{card.label}</p>
                    <p className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight truncate">{card.value}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5 truncate">{card.sub}</p>
                  </div>
                  <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", card.iconBg)}>
                    <card.icon className={cn("h-5 w-5", card.iconColor)} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ── MAIN CONTENT: 2-COLUMN LAYOUT ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4 sm:gap-5">

          {/* ── LEFT COLUMN ── */}
          <div className="space-y-4 sm:space-y-5">
            {/* Action Selector + Form combined card for Savings type */}
            {formType === "Savings" ? (
              <Card className="border border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden">
                <CardHeader className="px-5 pt-5 pb-0">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#009A44]/10">
                        <Wallet className="h-3.5 w-3.5 text-[#009A44]" />
                      </div>
                      Create New Saving
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-5 space-y-4">
                  {/* Amount Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                    <div className="sm:col-span-3 space-y-1.5">
                      <Label className="text-xs font-medium text-slate-600">
                        Amount <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          type="number"
                          placeholder="Enter amount to save"
                          value={formAmount}
                          onChange={(e) => { setFormAmount(e.target.value); setFormErrors((er) => ({ ...er, amount: "" })); }}
                          className={cn(
                            "h-10 rounded-lg text-sm font-semibold pr-14 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none border-slate-300 focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20",
                            formErrors.amount && "border-red-400"
                          )}
                          min="1"
                          onFocus={(e) => e.target.select()}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 pointer-events-none">FRW</span>
                      </div>
                      {formErrors.amount && <p className="text-xs text-red-500">{formErrors.amount}</p>}
                    </div>
                    <div className="sm:col-span-2 space-y-1.5">
                      <Label className="text-xs font-medium text-slate-600">Shares</Label>
                      <div className="h-10 rounded-lg border border-slate-300 bg-slate-50 px-3 flex items-center text-sm font-bold text-slate-700">
                        {shareValue > 0 ? Math.floor((parseFloat(formAmount) || 0) / shareValue) : 0} Shares
                      </div>
                      <p className="text-[10px] text-slate-400">1 Share = {shareValue.toLocaleString()} FRW</p>
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-600">
                      Payment Method <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: "Mobile Money", label: "Mobile Money", icon: Smartphone, iconColor: "text-[#16A34A]" },
                        { value: "Salary Deduction", label: "Salary Deduction", icon: Landmark, iconColor: "text-[#2563EB]" },
                        { value: "Cash", label: "Cash", icon: Banknote, iconColor: "text-[#F97316]" },
                      ].map((method) => (
                        <button
                          key={method.value}
                          type="button"
                          onClick={() => { setFormMethod(method.value); setFormErrors((e) => ({ ...e, method: "" })); }}
                          className={cn(
                            "flex flex-col items-center gap-1.5 rounded-xl border-2 p-3 text-center transition-all",
                            formMethod === method.value
                              ? "border-[#009A44] bg-[#009A44]/5 shadow-sm"
                              : "border-slate-200 bg-white hover:border-slate-300"
                          )}
                        >
                          <method.icon className={cn("h-5 w-5", formMethod === method.value ? method.iconColor : "text-slate-400")} />
                          <span className={cn("text-xs font-semibold leading-tight", formMethod === method.value ? "text-[#009A44]" : "text-slate-600")}>
                            {method.label}
                          </span>
                        </button>
                      ))}
                    </div>
                    {formErrors.method && <p className="text-xs text-red-500">{formErrors.method}</p>}
                  </div>

                  {/* Payment Date + Note Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-600">
                        Payment Date <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="date"
                        value={format(formDate, "yyyy-MM-dd")}
                        onChange={(e) => setFormDate(new Date(e.target.value + "T12:00:00"))}
                        className="h-10 rounded-lg text-sm border-slate-300 focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20"
                      />
                      {formErrors.date && <p className="text-xs text-red-500">{formErrors.date}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-600">Note (optional)</Label>
                      <Textarea
                        placeholder="Add a note..."
                        value={formNotes}
                        onChange={(e) => setFormNotes(e.target.value)}
                        className="rounded-lg border-slate-300 text-sm min-h-[40px] h-10 max-h-20 focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20 resize-none"
                        rows={1}
                      />
                    </div>
                  </div>

                  {/* Payment Proof */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-600">Payment Proof</Label>
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className={cn(
                        "rounded-xl border-2 border-dashed p-4 text-center cursor-pointer transition-all",
                        attachmentFile
                          ? "border-[#009A44] bg-[#009A44]/5"
                          : "border-slate-300 hover:border-slate-400 bg-slate-50/50 hover:bg-slate-100/50"
                      )}
                    >
                      {attachmentFile ? (
                        <div className="flex items-center justify-center gap-2">
                          <File className="h-5 w-5 text-[#009A44] shrink-0" />
                          <span className="text-xs text-slate-700 font-medium truncate max-w-[180px]">{attachmentFile.name}</span>
                          <span className="text-[10px] text-slate-400 shrink-0">({(attachmentFile.size / 1024 / 1024).toFixed(1)} MB)</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setAttachmentFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                            className="p-1 hover:bg-red-100 rounded-full text-red-500 shrink-0 transition-colors"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2.5">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200">
                            <Upload className="h-4 w-4 text-slate-500" />
                          </div>
                          <div className="text-left">
                            <p className="text-xs font-medium text-slate-600">Upload payment proof</p>
                            <p className="text-[10px] text-slate-400">JPG, PNG, PDF &bull; Max 5MB</p>
                          </div>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) { toast.error("File size must be less than 5MB"); return; }
                          setAttachmentFile(file);
                        }
                      }}
                      className="hidden"
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      variant="outline"
                      onClick={resetForm}
                      className="h-10 px-4 text-xs rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50"
                    >
                      Clear
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSubmit}
                      disabled={!isFormValid || recordMutation.isLoading || uploading}
                      className="h-10 flex-1 text-xs rounded-lg bg-[#009A44] hover:bg-[#00803a] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      {recordMutation.isLoading || uploading ? (
                        <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> {uploading ? "Uploading..." : "Submitting..."}</>
                      ) : (
                        <>
                          <Send className="h-3.5 w-3.5 mr-1.5" />
                          Submit Saving Request
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Info */}
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    <Info className="h-3.5 w-3.5 inline mr-1 -mt-0.5 text-blue-500" />
                    Your saving will be reviewed by the cooperative executives. Once approved, it will increase your savings and shares.
                  </p>
                </CardContent>
              </Card>
            ) : null}

            {/* Monthly Share Declaration Form */}
            {formType === "Monthly Share" && (
              <Card className="border border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden">
                <CardHeader className="px-5 pt-5 pb-0">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#2563EB]/10">
                        <PiggyBank className="h-3.5 w-3.5 text-[#2563EB]" />
                      </div>
                      Monthly Contribution
                    </CardTitle>
                    {monthlyStatus && (
                      <Badge className={cn(
                        "text-[10px] px-2 py-0.5 rounded-full font-medium",
                        monthlyStatus.canDeclare === false ? "bg-red-50 text-red-700 border border-red-200" :
                          monthlyStatus.isLateDeclaration ? "bg-orange-50 text-orange-700 border border-orange-200" :
                            monthlyStatus.status === "payroll_deducted" ? "bg-green-50 text-green-800 border border-green-200" :
                              "bg-slate-100 text-slate-600 border border-slate-200"
                      )}>
                        {monthlyStatus.canDeclare === false ? "Payroll Sent" :
                          monthlyStatus.isLateDeclaration ? "Late" :
                            monthlyStatus.statusLabel || "Not Declared"}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-5 space-y-4">
                  {loadingStatus ? (
                    <div className="grid grid-cols-3 gap-3">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="animate-pulse h-16 bg-slate-100 rounded-xl" />
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                          <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Minimum</p>
                          <p className="text-sm font-bold text-[#009A44] mt-0.5">{(monthlyStatus?.minimumAmount || REQUIRED_MONTHLY).toLocaleString()}</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                          <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Cutoff</p>
                          <p className="text-sm font-bold text-slate-900 mt-0.5">
                            {monthlyStatus?.cutoffDate ? format(new Date(monthlyStatus.cutoffDate), "dd MMM") : `Day ${monthlyStatus?.cutoffDay || 25}`}
                          </p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                          <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Approval</p>
                          <p className="text-sm font-bold text-slate-900 mt-0.5">{monthlyStatus?.approvalCount ?? 0}/{monthlyStatus?.requiredApprovalCount ?? 3}</p>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs font-medium text-slate-600">Declared Amount (FRW)</Label>
                          {!isEditing && (
                            <button type="button" onClick={() => setIsEditing(true)} className="text-[11px] font-medium text-[#009A44] hover:text-[#00803a] flex items-center gap-1">
                              <Pencil className="h-3 w-3" /> Edit
                            </button>
                          )}
                        </div>
                        {isEditing ? (
                          <div className="space-y-2">
                            <div className="relative max-w-xs">
                              <Input type="number" value={formAmount}
                                onChange={(e) => { setFormAmount(e.target.value); setFormErrors((er) => ({ ...er, amount: "" })); }}
                                className="h-10 rounded-lg text-sm font-semibold pr-14 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none border-slate-300 focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20"
                                min={monthlyStatus?.minimumAmount || REQUIRED_MONTHLY} autoFocus
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 pointer-events-none">FRW</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => { setIsEditing(false); setFormErrors((er) => ({ ...er, amount: "" })); }} className="text-[11px] text-slate-500 hover:text-slate-700 font-medium">Cancel</button>
                              <button type="button" onClick={() => setIsEditing(false)} className="text-[11px] font-medium text-[#009A44] hover:text-[#00803a]">Done</button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-base font-bold text-[#009A44]">
                            {(parseFloat(formAmount) || monthlyStatus?.minimumAmount || REQUIRED_MONTHLY).toLocaleString()} FRW
                          </p>
                        )}
                        {formErrors.amount && <p className="text-xs text-red-500">{formErrors.amount}</p>}
                      </div>

                      <p className="text-[11px] text-slate-500 leading-relaxed">
                        <Info className="h-3.5 w-3.5 inline mr-1 -mt-0.5 text-blue-500" />
                        {payrollEnabled
                          ? "After you declare, 3 leaders must approve. Once approved, HR processes the deduction from your salary."
                          : "After you declare, 3 leaders must approve. Once approved, you will need to submit payment manually."}
                      </p>

                      <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!isFormValid || recordMutation.isLoading}
                        className="h-10 w-full text-xs rounded-lg bg-[#009A44] hover:bg-[#00803a] text-white font-semibold disabled:opacity-50 shadow-sm"
                      >
                        {recordMutation.isLoading ? (
                          <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Submitting...</>
                        ) : monthlyStatus?.declaration && !["rejected", "cancelled"].includes(monthlyStatus.declaration.status) ? (
                          <><Send className="h-3.5 w-3.5 mr-1.5" /> Update Declaration</>
                        ) : (
                          <><Send className="h-3.5 w-3.5 mr-1.5" /> Submit Declaration</>
                        )}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Loan Repayment Form */}
            {formType === "Loan Repayment" && (
              <Card className="border border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden">
                <CardHeader className="px-5 pt-5 pb-0">
                  <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#7C3AED]/10">
                      <Receipt className="h-3.5 w-3.5 text-[#7C3AED]" />
                    </div>
                    Loan Repayment
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-5 space-y-3">
                  {loadingLoans ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500 py-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
                  ) : activeLoansData?.length > 0 ? (
                    <>
                      <p className="text-xs text-slate-500">Select a loan to repay:</p>
                      {(activeLoansData as any[]).map((loan: any) => (
                        <label key={loan.id} className={cn("block border-2 rounded-xl p-3.5 cursor-pointer transition-all", selectedLoanId === loan.id ? "border-[#7C3AED] bg-purple-50/50" : "border-slate-200 hover:border-slate-300")}>
                          <div className="flex items-start gap-3">
                            <input type="radio" name="loan" checked={selectedLoanId === loan.id} onChange={() => { setSelectedLoanId(loan.id); setFormErrors((e) => ({ ...e, type: "" })); }} className="mt-0.5 accent-[#7C3AED]" />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-slate-800">Loan #{loan.id}{loan.loanType ? ` - ${loan.loanType}` : ""}</p>
                              <div className="flex gap-3 mt-1 text-[11px]">
                                <span className="text-slate-500">{loan.amount?.toLocaleString()} FRW</span>
                                <span className="text-slate-400">Monthly: <strong>{loan.monthlyPayment?.toLocaleString()}</strong></span>
                                <span className="text-slate-400">Remaining: <strong>{loan.remainingBalance?.toLocaleString()}</strong></span>
                              </div>
                            </div>
                          </div>
                        </label>
                      ))}
                    </>
                  ) : (
                    <p className="text-xs text-slate-500 py-2">You have no active loan to repay.</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Fine/Penalty Form */}
            {formType === "Fine" && (
              <Card className="border border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden">
                <CardHeader className="px-5 pt-5 pb-0">
                  <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#F97316]/10">
                      <AlertTriangle className="h-3.5 w-3.5 text-[#F97316]" />
                    </div>
                    Fine / Penalty
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-5 space-y-3">
                  {loadingPenalties ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500 py-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading fines...</div>
                  ) : unpaidPenaltiesData?.length > 0 ? (
                    <>
                      <p className="text-xs text-slate-500">Select a fine to pay:</p>
                      {(unpaidPenaltiesData as any[]).map((penalty: any) => (
                        <label key={penalty.id} className={cn("block border-2 rounded-xl p-3.5 cursor-pointer transition-all", selectedPenaltyId === penalty.id ? "border-[#F97316] bg-orange-50/50" : "border-slate-200 hover:border-slate-300")}>
                          <div className="flex items-start gap-3">
                            <input type="radio" name="penalty" checked={selectedPenaltyId === penalty.id} onChange={() => { setSelectedPenaltyId(penalty.id); setFormErrors((e) => ({ ...e, type: "" })); }} className="mt-0.5 accent-[#F97316]" />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-sm font-semibold text-slate-800">{penalty.reason || penalty.penaltyType || "Fine"}</p>
                                <span className="text-sm font-bold text-red-600 shrink-0">{penalty.amount?.toLocaleString()} FRW</span>
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5">Type: {penalty.penaltyType || "General"}</p>
                            </div>
                          </div>
                        </label>
                      ))}
                    </>
                  ) : (
                    <p className="text-xs text-slate-500 py-2">You have no unpaid fines.</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Action Selector (only show when not Savings type, for switching types) */}
            {formType !== "Savings" && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => { setFormType("Savings"); setFormAmount(""); setFormErrors({}); }}
                  className="text-xs font-medium text-[#009A44] hover:text-[#00803a] px-3 py-1.5 rounded-lg border border-[#009A44]/30 hover:bg-[#009A44]/5 transition-colors"
                >
                  Switch to Add Saving
                </button>
              </div>
            )}
          </div>

          {/* ── RIGHT COLUMN (sticky on desktop) ── */}
          <div className="space-y-4 sm:space-y-5 lg:sticky lg:top-5 lg:self-start">
            {/* Info Banner */}
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20">
                  <Info className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white">Savings vs Contributions</p>
                  <p className="text-xs text-blue-100 mt-0.5">Only voluntary savings can be withdrawn. Contributions deducted from salary are locked.</p>
                </div>
              </div>
            </div>

            {/* Savings Overview */}
            <Card className="border border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden">
              <CardHeader className="px-5 pt-5 pb-0">
                <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-50">
                    <Wallet className="h-3.5 w-3.5 text-emerald-600" />
                  </div>
                  Savings Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5">
                <div className="space-y-0 divide-y divide-slate-100">
                  <div className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                    <span className="text-xs font-semibold text-emerald-700">Total in Account (All Savings)</span>
                    <span className="text-sm font-bold text-emerald-600">{totalAllSavings.toLocaleString()} FRW</span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <span className="text-xs text-slate-500">Pending Savings</span>
                    <span className="text-sm font-bold text-slate-900">{pendingSavings.toLocaleString()} FRW</span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <span className="text-xs text-slate-500">Total This Year</span>
                    <span className="text-sm font-bold text-slate-900">{thisYearTotalSavings.toLocaleString()} FRW</span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <span className="text-xs text-slate-500">Shares Earned</span>
                    <span className="text-sm font-bold text-slate-900">{savingsShares} Share{savingsShares !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <span className="text-xs font-semibold text-green-700">Available to Withdraw</span>
                    <span className="text-sm font-bold text-green-600">{voluntarySavings.toLocaleString()} FRW</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions hint */}
            <div className="hidden lg:block text-center">
              <p className="text-[11px] text-slate-400">
                <ChevronRight className="h-3 w-3 inline mr-0.5" />
                Use the form on the left to submit a new saving
              </p>
            </div>
          </div>
        </div>

        {/* ── SAVINGS HISTORY ── */}
        <Card className="border border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden">
          <CardHeader className="px-5 pt-5 pb-0">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-sm font-semibold text-slate-900">My Savings History</CardTitle>
              <div className="flex items-center gap-2">
                <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                  {(["all", "pending", "approved", "rejected"] as const).map((filter) => (
                    <button
                      key={filter}
                      type="button"
                      onClick={() => setHistoryStatusFilter(filter)}
                      className={cn(
                        "px-2.5 py-1 text-[11px] font-medium rounded-md transition-all",
                        historyStatusFilter === filter
                          ? "bg-white text-slate-900 shadow-sm"
                          : "text-slate-500 hover:text-slate-700"
                      )}
                    >
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    className="h-8 pl-8 pr-2.5 text-xs rounded-lg border border-slate-200 bg-white focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20 outline-none w-32 sm:w-40"
                  />
                </div>
                {filteredHistory.length > 0 && (
                  <span className="text-[11px] text-slate-400 font-medium shrink-0">{filteredHistory.length}</span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loadingSavingsData ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-[#009A44]" />
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 mb-3">
                  <Wallet className="h-6 w-6 text-slate-300" />
                </div>
                <p className="text-sm font-medium text-slate-500">No savings records found</p>
                <p className="text-xs text-slate-400 mt-0.5">Submit your first saving using the form above.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/80">
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                      <th className="px-3 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                      <th className="px-3 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Shares</th>
                      <th className="px-3 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Method</th>
                      <th className="px-3 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Proof</th>
                      <th className="px-3 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Note</th>
                      <th className="px-3 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-3 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Approved By</th>
                      <th className="px-3 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Approval Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.slice(0, 50).map((row) => {
                      const normalized = String(row.status || "").toLowerCase();
                      const displayDate = row.date && !Number.isNaN(new Date(row.date).getTime()) ? format(new Date(row.date), "dd MMM yyyy") : "-";
                      const approvalDate = row.approvalDate && !Number.isNaN(new Date(row.approvalDate).getTime()) ? format(new Date(row.approvalDate), "dd MMM yyyy") : "-";
                      const isPending = normalized.includes("pending") || normalized === "pending_approval";
                      const isRejected = normalized.includes("rejected") || normalized.includes("cancelled");
                      return (
                        <tr key={row.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors">
                          <td className="px-5 py-3 text-slate-700 whitespace-nowrap text-xs">{displayDate}</td>
                          <td className="px-3 py-3 text-right font-semibold text-slate-900 whitespace-nowrap text-xs">{row.amount.toLocaleString()} FRW</td>
                          <td className="px-3 py-3 text-slate-700 whitespace-nowrap text-xs">{row.shares} Share{row.shares === 1 ? "" : "s"}</td>
                          <td className="px-3 py-3 text-slate-600 whitespace-nowrap text-xs">{row.method}</td>
                          <td className="px-3 py-3 text-center">
                            {row.proof ? (
                              <a href={row.proof} target="_blank" rel="noopener noreferrer" className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors">
                                <Eye className="h-3 w-3" />
                              </a>
                            ) : (
                              <span className="text-xs text-slate-300">-</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-slate-500 max-w-[140px] truncate text-xs">{row.note || "-"}</td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <Badge className={cn(
                              "text-[10px] font-medium border rounded-full px-2 py-0.5",
                              isRejected ? "bg-red-50 text-red-700 border-red-200" :
                                isPending ? "bg-amber-50 text-amber-700 border-amber-200" :
                                  "bg-green-50 text-green-700 border-green-200"
                            )}>
                              {isPending ? "Pending" : isRejected ? "Rejected" : "Approved"}
                            </Badge>
                          </td>
                          <td className="px-3 py-3 text-slate-500 whitespace-nowrap text-xs">{row.approvedBy || "-"}</td>
                          <td className="px-3 py-3 text-slate-500 whitespace-nowrap text-xs">{approvalDate}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
